/* ===========================================================================
   Pencilled Paws AU — theme behaviour

   No dependencies, no third-party requests, no tracking.
   Everything degrades without JavaScript: the order form is a plain
   multipart POST to /cart/add, the FAQ falls back to open answers, and
   the cart updates through normal form submits.
   =========================================================================== */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------ mobile drawer -- */
  function initDrawer() {
    var drawer = document.querySelector('[data-ppa-drawer]');
    if (!drawer) return;

    var openers = document.querySelectorAll('[data-ppa-drawer-open]');
    var closers = drawer.querySelectorAll('[data-ppa-drawer-close]');
    var panel = drawer.querySelector('.ppa-drawer__panel');
    var lastFocused = null;

    function focusables() {
      return panel
        ? panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea')
        : [];
    }

    function open() {
      lastFocused = document.activeElement;
      drawer.dataset.open = 'true';
      drawer.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
      var first = focusables()[0];
      if (first) first.focus();
    }

    function close() {
      drawer.dataset.open = 'false';
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    Array.prototype.forEach.call(openers, function (b) {
      b.addEventListener('click', open);
    });
    Array.prototype.forEach.call(closers, function (b) {
      b.addEventListener('click', close);
    });
    Array.prototype.forEach.call(drawer.querySelectorAll('a'), function (a) {
      a.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (drawer.dataset.open !== 'true') return;

      if (e.key === 'Escape') {
        close();
        return;
      }

      /* Keep tab focus inside the open drawer. */
      if (e.key === 'Tab') {
        var items = focusables();
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* ---------------------------------------------------- reveal on scroll -- */
  function initReveal() {
    var els = document.querySelectorAll('.ppa-reveal');
    if (!els.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    Array.prototype.forEach.call(els, function (el) {
      io.observe(el);
    });
  }

  /* -------------------------------------------------------------- the FAQ - */
  function initFaq() {
    var items = document.querySelectorAll('[data-ppa-faq-item]');
    Array.prototype.forEach.call(items, function (item) {
      var btn = item.querySelector('[data-ppa-faq-q]');
      var answer = item.querySelector('[data-ppa-faq-a]');
      if (!btn || !answer) return;

      /* Only collapse once JS is available, so no-JS keeps answers visible. */
      if (item.dataset.open !== 'true') {
        item.dataset.open = 'false';
        btn.setAttribute('aria-expanded', 'false');
      } else {
        btn.setAttribute('aria-expanded', 'true');
      }

      btn.addEventListener('click', function () {
        var open = item.dataset.open === 'true';
        item.dataset.open = open ? 'false' : 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* --------------------------------------------------- the order form ----- */
  function initProduct() {
    var root = document.querySelector('[data-ppa-product]');
    if (!root) return;

    var form = root.querySelector('[data-ppa-form]');
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    var priceEls = document.querySelectorAll('[data-ppa-price]');
    var maxMb = parseInt(root.dataset.maxUploadMb || '20', 10);
    var maxBytes = maxMb * 1024 * 1024;

    /* -- variant rows keep the hidden id and every price display in sync -- */
    var variantData = {};
    var json = root.querySelector('[data-ppa-variants]');
    if (json) {
      try {
        JSON.parse(json.textContent).forEach(function (v) {
          variantData[v.id] = v;
        });
      } catch (e) {
        /* leave the server-rendered price alone */
      }
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-ppa-variant]'), function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        if (idInput) idInput.value = input.value;
        var v = variantData[input.value];
        if (v && v.price_formatted) {
          Array.prototype.forEach.call(priceEls, function (el) {
            el.textContent = v.price_formatted;
          });
        }
      });
    });

    /* ---------------------------------------- the customer's pet photo ---- */
    var zone = root.querySelector('[data-ppa-upload]');
    if (zone) {
      var input = zone.querySelector('input[type="file"]');
      var preview = zone.querySelector('[data-ppa-preview] img');
      var nameEl = zone.querySelector('[data-ppa-filename]');
      var errEl = root.querySelector('[data-ppa-upload-error]');

      var fail = function (message) {
        if (errEl) {
          errEl.textContent = message;
          errEl.dataset.show = 'true';
        }
        zone.dataset.hasFile = 'false';
        if (input) input.value = '';
      };

      var clearError = function () {
        if (errEl) errEl.dataset.show = 'false';
      };

      var handle = function (file) {
        clearError();
        if (!file) return;

        /* Some phones report an empty type for HEIC, so check the name too. */
        var looksLikeImage =
          /^image\//.test(file.type) || /\.(jpe?g|png|heic|heif|webp)$/i.test(file.name || '');

        if (!looksLikeImage) {
          fail('That file is not a photo. Please upload a JPG, PNG, HEIC or WebP image.');
          return;
        }

        if (file.size > maxBytes) {
          fail('That photo is a little too large. Please upload one under ' + maxMb + 'MB.');
          return;
        }

        if (nameEl) nameEl.textContent = file.name;
        zone.dataset.hasFile = 'true';

        if (preview && window.FileReader && /^image\//.test(file.type)) {
          var reader = new FileReader();
          reader.onload = function (e) {
            preview.src = e.target.result;
          };
          reader.readAsDataURL(file);
        }
      };

      if (input) {
        input.addEventListener('change', function () {
          handle(input.files && input.files[0]);
        });
      }

      ['dragenter', 'dragover'].forEach(function (t) {
        zone.addEventListener(t, function (e) {
          e.preventDefault();
          zone.dataset.drag = 'true';
        });
      });

      ['dragleave', 'drop'].forEach(function (t) {
        zone.addEventListener(t, function (e) {
          e.preventDefault();
          zone.dataset.drag = 'false';
        });
      });

      zone.addEventListener('drop', function (e) {
        var files = e.dataTransfer && e.dataTransfer.files;
        if (!files || !files.length) return;
        if (input) {
          try {
            input.files = files;
          } catch (err) {
            /* older browsers: the customer can still use the file picker */
          }
        }
        handle(files[0]);
      });

      /* Never let an order reach checkout without the photo attached. */
      if (form) {
        form.addEventListener('submit', function (e) {
          if (input && input.required && !(input.files && input.files.length)) {
            e.preventDefault();
            fail('Please add a photo of your pet so we know who we are drawing.');
            zone.scrollIntoView({
              behavior: reduceMotion ? 'auto' : 'smooth',
              block: 'center'
            });
          }
        });
      }
    }

    /* ----------------------------------------- sticky mobile add to cart -- */
    var sticky = root.querySelector('[data-ppa-sticky]');
    var anchor = root.querySelector('[data-ppa-atc-anchor]');
    if (sticky && anchor && 'IntersectionObserver' in window) {
      var so = new IntersectionObserver(
        function (entries) {
          sticky.dataset.show = entries[0].isIntersecting ? 'false' : 'true';
        },
        { rootMargin: '-120px 0px 0px 0px' }
      );
      so.observe(anchor);
    }
  }

  /* ------------------------------------------------------- cart quantity -- */
  function initCart() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-ppa-qty-input]'),
      function (input) {
        input.addEventListener('change', function () {
          var form = input.closest('form');
          if (form) form.submit();
        });
      }
    );
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  ready(function () {
    initDrawer();
    initReveal();
    initFaq();
    initProduct();
    initCart();
  });
})();
